<template>
  <div class="allocate-device">
    <div class="header">
      <h1>分配设备</h1>
      <button class="back-btn" @click="handleBack">
        <el-icon><ArrowLeft /></el-icon>
      </button>
    </div>
    
    <div class="content">
      <div class="form-item">
        <div class="label">设备规格：</div>
        <el-select
          v-model="selectedCategory"
          filterable
          clearable
          placeholder="请选择设备规格"
          @change="selectDeviceCategory"
          class="select-full"
        >
          <el-option
            v-for="item in categoryList"
            :key="item.id"
            :label="item.nickName"
            :value="item.id"
          ></el-option>
        </el-select>
      </div>
      

      
      <div class="device-info">
        <p class="info-row">
          <span>开数：{{ selectedCategoryObj.cloudMachinesOpenNum || "--" }}</span>
          <span>已选中设备：{{ selectedDevices.length }}</span>
        </p>
      </div>
      
      <div class="device-list" v-loading="tableLoading">
        <div class="list-header">
          <div class="col-checkbox">
            <el-checkbox v-model="selectAll" @change="handleSelectAll"></el-checkbox>
          </div>
          <div class="col-number">设备物理编号</div>
          <div class="col-status">状态</div>
          <div class="col-open">开数</div>
        </div>
        
        <div class="list-body">
          <div 
            v-for="item in deviceData" 
            :key="item.cloudMachineNumber"
            class="list-item"
          >
            <div class="col-checkbox">
              <el-checkbox 
                v-model="item.selected" 
                @change="handleSelectChange"
              ></el-checkbox>
            </div>
            <div class="col-number">{{ item.cloudMachineNumber }}</div>
            <div class="col-status">{{ getStatusText(item.status) }}</div>
            <div class="col-open">{{ item.cloudMachinesOpenNum || "--" }}</div>
          </div>
          <div v-if="deviceData.length === 0" class="empty-list">
            暂无数据
          </div>
        </div>
        
        <!-- 分页组件 -->
        <div class="pagination">
          <el-pagination
            v-model:current-page="pagination.currentPage"
            v-model:page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100, 200]"
            layout="total, sizes, prev, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            size="small"
            type="simple"
          />
        </div>
      </div>
      
      <div class="form-item" style="margin-top: 20px;">
        <div class="label">选择机型：</div>
        <el-select 
          v-model="selectedDeviceModel" 
          filterable 
          clearable 
          placeholder="请选择设备机型"
          class="select-full"
          @visible-change="modelVisibleChange"
        >
          <el-option-group
            v-for="group in modelList"
            :key="group.label"
            :label="group.label"
          >
            <el-option
              v-for="item in group.children"
              :key="item.id"
              :label="item.modelName"
              :value="item.id"
            />
          </el-option-group>
        </el-select>
      </div>
      
      <div class="tips">
        <el-icon color="orange" size="16"><InfoFilled /></el-icon>
        <span>提示：设置设备机型需所有设备为同一镜像版本</span>
      </div>
      
      <div class="checkbox-item">
        <el-checkbox v-model="isClearZero">
          库存清零（已分配规格设备更换规格将修改<br/>原规格下sku库存）
        </el-checkbox>
      </div>
    </div>
    
    <div class="footer">
      <el-button 
        type="primary" 
        class="submit-btn"
        @click="submit"
        :loading="loading"
        :disabled="loading"
      >
        确 定
      </el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, defineEmits, onMounted } from "vue";
import API_CONFIG from "@/views/productMgt/deviceResourceAllocation/api.json";
import request from "@/utils/request.ts";
import { ElMessage } from "element-plus";
import debounce from 'lodash-es/debounce.js';
import { useRouter } from "vue-router";

const router = useRouter();
const emit = defineEmits(["reload"]);

// 状态管理
const selectedCategory = ref("");
const selectedCategoryObj = ref({});
const deviceData = ref([]);
const loading = ref(false);
const categoryList = ref([]);
const modelList = ref([]);
const selectedDeviceModel = ref("");
const isClearZero = ref(false);
const selectAll = ref(false);
const selectedDevices = ref([]);
const tableLoading = ref(false);



// 分页参数
const pagination = ref({
  currentPage: 1,
  pageSize: 10,
  total: 0
});

// 初始化数据
const initData = () => {
  getCategoryList();
};

// 获取规格列表
const getCategoryList = () => {
  request.post(API_CONFIG.queryCategory.url).then((res) => {
    if (res.code == 200) {
      categoryList.value = (res.data || []).filter(ele => ele.status == '0');
      // 默认选择第一个规格
      if (categoryList.value.length > 0) {
        selectedCategory.value = categoryList.value[0].id;
        selectedCategoryObj.value = categoryList.value[0];
        // 加载设备列表
        getDeviceList();
      }
    } else {
      categoryList.value = [];
    }
  });
};

// 获取设备列表
const getDeviceList = () => {
  tableLoading.value = true;
  request.post(API_CONFIG.queryUrl.url, {
    pageDomain:{
      pageNum: pagination.value.currentPage,
      pageSize: pagination.value.pageSize
    },
    status: '0',
    categoryId: selectedCategory.value
  }).then((res) => {
    if (res.code == 200) {
      deviceData.value = (res.data.data || []).map(item => ({
        ...item,
        selected: false
      }));
      pagination.value.total = res.data.total || 0;
      selectAll.value = false;
      selectedDevices.value = [];
    } else {
      deviceData.value = [];
      pagination.value.total = 0;
    }
  }).finally(() => {
    tableLoading.value = false;
  });
};

// 选择设备规格
const selectDeviceCategory = (val) => {
  const selectCategory = categoryList.value.find((ele) => ele.id == val);
  selectedCategoryObj.value = selectCategory;
  // 切换规格时重新加载设备列表
  getDeviceList();
};

// 处理分页大小变化
const handleSizeChange = (size) => {
  pagination.value.pageSize = size;
  getDeviceList();
};

// 处理页码变化
const handleCurrentChange = (current) => {
  pagination.value.currentPage = current;
  getDeviceList();
};

// 全选/取消全选
const handleSelectAll = (value) => {
  deviceData.value.forEach(item => {
    item.selected = value;
  });
  updateSelectedDevices();
};

// 单个选择
const handleSelectChange = () => {
  selectAll.value = deviceData.value.every(item => item.selected);
  updateSelectedDevices();
};

// 更新选中的设备
const updateSelectedDevices = () => {
  selectedDevices.value = deviceData.value.filter(item => item.selected);
};

// 获取状态文本
const getStatusText = (status) => {
  const statusMap = {
    '0': '空闲',
    '1': '回收中',
    '2': '清退',
    '3': '售出',
    '4': '异常设备'
  };
  return statusMap[status] || '--';
};

// 提交
const submit = debounce(async () => {
  if (!selectedCategory.value) {
    ElMessage.warning("请选择一个规格");
    return;
  }
  
  if (selectedDevices.length === 0) {
    ElMessage.warning("请至少选择一台设备");
    return;
  }

  loading.value = true;
  
  try {
    const res = await request.post(API_CONFIG.addUrl.url, {
      categoryId: selectedCategory.value,
      cloudMachineNumberList: selectedDevices.value.map(ele => ele.cloudMachineNumber),
      isClearZero: isClearZero.value ? '0' : '1'
    });
    
    if (res.code == 200) {
      ElMessage.success(`绑定设备成功`);
      await bindModelForDevice();
    }
  } catch (e) {
    ElMessage.error(e.message);
  } finally {
    loading.value = false;
  }
}, 500);

// 给分配的设备绑定机型
const bindModelForDevice = async () => {
  if (!selectedDeviceModel.value) {
    emit("reload");
    handleBack();
    return;
  }
  
  const paramsData = [{
    modelPhoneId: selectedDeviceModel.value,
    deviceIds: selectedDevices.value.map((ele) => ele.cloudMachineNumber)
  }];

  try {
    await request.post(API_CONFIG.batchChangeModel.url, paramsData);
  } catch (e) {
    console.error("绑定机型失败:", e);
  } finally {
    emit("reload");
    handleBack();
  }
};

// 机型选择框显示时获取机型列表
const modelVisibleChange = (visible) => {
  if (visible) {
    const groupedDevices = groupDevicesById(selectedDevices.value);
    if(groupedDevices.length == 1){
       getModelListFn(selectedDevices.value[0].mirrorVersion)
    }else{
      modelList.value = []
      ElMessage.warning("请选择相同镜像版本的设备");
    }
  }
};

// 根据镜像版本获取机型列表
const getModelListFn = (version) => {
  request.post(API_CONFIG.getModelList.url, {
    mirrorVersion: version
  })
    .then((res) => {
      modelList.value = getModelCategory(res.data || []);
    })
    .catch((e) => {
      ElMessage.error(e.message);
    });
};

// 处理机型列表参数
const getModelCategory = (phoneModels) => {
  const grouped = {}
  phoneModels.forEach((model) => {
    if (!grouped[model.brandName]) {
      grouped[model.brandName] = {
        label: model.brandName,
        value: model.brandName,
        children: []
      }
    }
    grouped[model.brandName].children.push(model)
  })
  // 将分组对象转换为数组
  return Object.values(grouped)
};

// 根据镜像将设备分组
const groupDevicesById = (devices) => {
  const grouped = {}
  // 遍历设备并按id分组
  devices.forEach((device) => {
    if (!grouped[device.mirrorVersion]) {
      grouped[device.mirrorVersion] = {
        label: device.mirrorVersion,
        value: device.mirrorVersion,
        phoneModelId: '',
        phoneModelName:'',
        children: []
      }
    }
    grouped[device.mirrorVersion].children.push(device)
  })
  // 将分组对象转换为数组
  return Object.values(grouped)
};

// 返回
const handleBack = () => {
   router.push('/h5/workPlace');
};

// 页面加载时初始化数据
onMounted(() => {
  initData();
});
</script>

<style scoped lang="scss">
.allocate-device {
  height: 100vh;
  background-color: #f5f7fa;
  overflow: auto;
  .header {
    position: relative;
    background-color: #409eff;
    color: white;
    padding: 16px;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    h1 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
    
    .back-btn {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: white;
      font-size: 20px;
      cursor: pointer;
      padding: 0;
    }
  }
  
  .content {
    flex: 1;
    height: calc(100% - 225px);
    padding: 16px;
    overflow-y: auto;
    
    .form-item {
      margin-bottom: 16px;
      
      .label {
        margin-bottom: 8px;
        font-size: 14px;
        color: #333;
      }
      
      .select-full {
        width: 100%;
      }
    }
    
    .device-info {
      background-color: #fff;
      padding: 16px;
      border-radius: 8px;
      margin-bottom: 16px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      
      .info-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        font-size: 14px;
        
        span {
          &:first-child {
            color: #666;
          }
          &:last-child {
            color: #333;
            font-weight: 500;
          }
        }
      }
    }
    
    .device-list {
      background-color: #fff;
      border-radius: 8px;
      overflow: hidden;
      margin-bottom: 16px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      
      .list-header {
        display: flex;
        align-items: center;
        padding: 10px 16px;
        background-color: #f9f9f9;
        border-bottom: 1px solid #eee;
        font-size: 13px;
        font-weight: 500;
        
        .col-checkbox {
          width: 40px;
        }
        
        .col-number {
          flex: 1;
        }
        
        .col-status {
          width: 70px;
          text-align: center;
        }
        
        .col-open {
          width: 60px;
          text-align: center;
        }
      }
      
      .list-body {
        max-height: 400px;
        overflow: auto;
        .list-item {
          display: flex;
          align-items: center;
          padding: 10px 16px;
          border-bottom: 1px solid #eee;
          font-size: 13px;
          
          &:last-child {
            border-bottom: none;
          }
          
          .col-checkbox {
            width: 40px;
          }
          
          .col-number {
            flex: 1;
            color: #333;
          }
          
          .col-status {
            width: 70px;
            text-align: center;
            
            &.status-idle {
              color: #67c23a;
            }
            
            &.status-using {
              color: #409eff;
            }
          }
          
          .col-open {
            width: 60px;
            text-align: center;
            color: #333;
          }
        }
        
        .empty-list {
          padding: 40px 0;
          text-align: center;
          color: #999;
          font-size: 14px;
        }
      }
      
      .pagination {
        padding: 12px 16px;
        border-top: 1px solid #eee;
        display: flex;
        justify-content: center;
      }
    }
    
    .tips {
      display: flex;
      align-items: center;
      color: orange;
      font-size: 12px;
      margin: 12px 0;
      padding: 10px;
      background-color: #fff9e6;
      border-radius: 4px;
      
      span {
        margin-left: 8px;
      }
    }
    
    .checkbox-item {
      margin-top: 16px;
      font-size: 14px;
      background-color: #fff;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }
  }
  
  .footer {
    padding: 16px;
    background-color: white;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    .submit-btn {
      width: 100%;
      height: 44px;
      font-size: 16px;
    }
  }
}

// 响应式调整
@media (max-width: 375px) {
  .allocate-device {
    .content {
      .device-list {
        .list-header,
        .list-item {
          .col-status {
            width: 60px;
          }
          
          .col-open {
            width: 50px;
          }
        }
      }
    }
  }
}
</style>